import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificacionesAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Bell, Check, CheckCheck, Trash2, Loader2, FileText, AlertTriangle, Info,
  Users, FilePlus, RefreshCw, ExternalLink, Bookmark, ChevronDown, ChevronUp,
  CreditCard, Sparkles, MailX, Squircle, SquareCheckBig,
} from 'lucide-react';

const PREVIEW_COUNT = 4;

// Mapeo defensivo: tipo de notificación → destino seguro en el frontend.
// Tiene prioridad sobre `notif.link` para corregir links históricos rotos.
// Si retorna null, se cae en `notif.link` como fallback.
const getDestinoSeguro = (notif) => {
  if (!notif) return null;
  const tipo = notif.tipo;

  // Cobranza: siempre abre el modal de "Pagar Ahora" en el panel del taller
  if (['vencimiento_proximo', 'suscripcion_vencida', 'suscripcion_suspendida'].includes(tipo)) {
    return '/panel?pago=1';
  }

  // Pago recibido / confirmaciones
  if (['pago_recibido', 'pago_suscripcion_recibido'].includes(tipo)) {
    return '/panel';
  }

  // Documentos: si tiene documento_id, abre el visor
  if (['nuevo_documento', 'actualizacion_manual'].includes(tipo)) {
    const docId = notif.documento_id;
    if (docId) return `/panel/documentos?abrir=${docId}`;
    // Si el link guardado en BD apunta a la ruta vieja, lo reescribimos
    if (notif.link && notif.link.startsWith('/documentos/manual/')) {
      const id = notif.link.split('/documentos/manual/')[1].split('?')[0];
      return `/panel/documentos?abrir=${id}`;
    }
    return '/panel/documentos';
  }

  // Marcador compartido viene con un link bien armado, lo respetamos
  if (tipo === 'marcador_compartido' && notif.link) return notif.link;

  // Solicitudes / admin
  if (tipo === 'solicitud_pendiente') return '/admin/solicitudes';
  if (tipo === 'cambio_suscripcion') return '/admin/talleres';
  if (tipo === 'inscripcion_curso') return '/admin/capacitaciones';

  // Sistema y otros: si el link es válido (no rutas legacy), usarlo
  if (notif.link && !['/dashboard', '/taller/pagos', '/'].includes(notif.link)) {
    return notif.link;
  }

  // Fallback final: panel
  return '/panel';
};

const ICONO_POR_TIPO = {
  marcador_compartido: <Bookmark className="h-5 w-5 text-amber-500" />,
  nuevo_documento: <FilePlus className="h-5 w-5 text-emerald-500" />,
  actualizacion_manual: <RefreshCw className="h-5 w-5 text-blue-500" />,
  directiva_ad: <AlertTriangle className="h-5 w-5 text-amber-500" />,
  documento: <FileText className="h-5 w-5 text-blue-500" />,
  alerta: <AlertTriangle className="h-5 w-5 text-amber-500" />,
  usuario: <Users className="h-5 w-5 text-green-500" />,
  solicitud_pendiente: <Users className="h-5 w-5 text-orange-500" />,
  vencimiento_proximo: <CreditCard className="h-5 w-5 text-orange-500" />,
  suscripcion_vencida: <AlertTriangle className="h-5 w-5 text-red-500" />,
  suscripcion_suspendida: <AlertTriangle className="h-5 w-5 text-red-600" />,
  pago_recibido: <Check className="h-5 w-5 text-emerald-600" />,
  pago_suscripcion_recibido: <Check className="h-5 w-5 text-emerald-600" />,
  cambio_suscripcion: <Sparkles className="h-5 w-5 text-violet-500" />,
  inscripcion_curso: <Sparkles className="h-5 w-5 text-violet-500" />,
  sistema: <Info className="h-5 w-5 text-gray-500" />,
};

const TIPOS_COBRANZA = ['vencimiento_proximo', 'suscripcion_vencida', 'suscripcion_suspendida'];

const NotificacionesPage = () => {
  useAuth();
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verTodas, setVerTodas] = useState(false);
  const [modoSeleccion, setModoSeleccion] = useState(false);
  const [seleccionadas, setSeleccionadas] = useState(new Set());
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    loadNotificaciones();
  }, []);

  const loadNotificaciones = async () => {
    try {
      const response = await notificacionesAPI.getAll();
      setNotificaciones(response.data || []);
    } catch (error) {
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const marcarLeida = async (id) => {
    try {
      await notificacionesAPI.marcarLeida(id);
      setNotificaciones((prev) =>
        prev.map((n) => (n.id === id ? { ...n, leida: true } : n))
      );
    } catch (error) {
      toast.error('Error al marcar como leída');
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      await notificacionesAPI.marcarTodasLeidas();
      setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
      toast.success('Todas marcadas como leídas');
    } catch (error) {
      toast.error('Error al marcar notificaciones');
    }
  };

  const eliminar = async (id) => {
    try {
      await notificacionesAPI.eliminar(id);
      setNotificaciones((prev) => prev.filter((n) => n.id !== id));
      setSeleccionadas((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Notificación eliminada');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const eliminarSeleccionadas = async () => {
    if (seleccionadas.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(seleccionadas);
      await notificacionesAPI.eliminarMultiples(ids);
      setNotificaciones((prev) => prev.filter((n) => !seleccionadas.has(n.id)));
      setSeleccionadas(new Set());
      setModoSeleccion(false);
      toast.success(`${ids.length} notificaciones eliminadas`);
    } catch (error) {
      toast.error('Error al eliminar seleccionadas');
    } finally {
      setBulkLoading(false);
    }
  };

  const marcarSeleccionadasLeidas = async () => {
    if (seleccionadas.size === 0) return;
    setBulkLoading(true);
    try {
      const ids = Array.from(seleccionadas);
      await notificacionesAPI.marcarMultiplesLeidas(ids);
      setNotificaciones((prev) =>
        prev.map((n) => (seleccionadas.has(n.id) ? { ...n, leida: true } : n))
      );
      setSeleccionadas(new Set());
      setModoSeleccion(false);
      toast.success(`${ids.length} marcadas como leídas`);
    } catch (error) {
      toast.error('Error al marcar seleccionadas');
    } finally {
      setBulkLoading(false);
    }
  };

  const eliminarTodas = async () => {
    setConfirmDeleteAll(false);
    setBulkLoading(true);
    try {
      await notificacionesAPI.eliminarTodas();
      setNotificaciones([]);
      setSeleccionadas(new Set());
      setModoSeleccion(false);
      toast.success('Bandeja vaciada');
    } catch (error) {
      toast.error('Error al vaciar bandeja');
    } finally {
      setBulkLoading(false);
    }
  };

  const toggleSeleccion = (id) => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const seleccionarTodas = () => {
    if (seleccionadas.size === visibles.length) {
      setSeleccionadas(new Set());
    } else {
      setSeleccionadas(new Set(visibles.map((n) => n.id)));
    }
  };

  const handleClick = (notif) => {
    if (modoSeleccion) {
      toggleSeleccion(notif.id);
      return;
    }
    if (!notif.leida) marcarLeida(notif.id);
    const destino = getDestinoSeguro(notif);
    if (destino) navigate(destino);
  };

  const handlePagarShortcut = (e, notif) => {
    e.stopPropagation();
    if (!notif.leida) marcarLeida(notif.id);
    navigate('/panel?pago=1');
  };

  const getIcono = (tipo) => ICONO_POR_TIPO[tipo] || <Info className="h-5 w-5 text-gray-500" />;

  const noLeidas = notificaciones.filter((n) => !n.leida).length;
  const visibles = useMemo(
    () => (verTodas ? notificaciones : notificaciones.slice(0, PREVIEW_COUNT)),
    [verTodas, notificaciones]
  );
  const ocultas = notificaciones.length - visibles.length;
  const todasSeleccionadas = visibles.length > 0 && seleccionadas.size === visibles.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-100 rounded-full">
              <Bell className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold leading-tight">Notificaciones</h1>
              <p className="text-sm text-gray-500">
                {notificaciones.length === 0
                  ? 'Sin notificaciones'
                  : `${notificaciones.length} en total${noLeidas > 0 ? ` · ${noLeidas} sin leer` : ''}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="notif-toolbar">
            {notificaciones.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setModoSeleccion((v) => !v);
                  setSeleccionadas(new Set());
                }}
                data-testid="toggle-seleccion-btn"
              >
                {modoSeleccion ? (
                  <><Squircle className="h-4 w-4 mr-2" /> Cancelar</>
                ) : (
                  <><SquareCheckBig className="h-4 w-4 mr-2" /> Seleccionar</>
                )}
              </Button>
            )}
            {noLeidas > 0 && !modoSeleccion && (
              <Button variant="outline" size="sm" onClick={marcarTodasLeidas} data-testid="marcar-todas-btn">
                <CheckCheck className="h-4 w-4 mr-2" />
                Marcar todas como leídas
              </Button>
            )}
            {notificaciones.length > 0 && !modoSeleccion && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDeleteAll(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="eliminar-todas-btn"
              >
                <MailX className="h-4 w-4 mr-2" />
                Vaciar bandeja
              </Button>
            )}
          </div>
        </div>

        {/* Barra de acciones masivas */}
        {modoSeleccion && (
          <Card className="mb-4 border-sky-200 bg-sky-50/60">
            <CardContent className="py-3 px-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={todasSeleccionadas}
                  onCheckedChange={seleccionarTodas}
                  data-testid="seleccionar-todas-cb"
                />
                <span className="text-sm font-medium text-gray-700">
                  {seleccionadas.size === 0
                    ? 'Seleccioná notificaciones para actuar'
                    : `${seleccionadas.size} seleccionada${seleccionadas.size === 1 ? '' : 's'}`}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={marcarSeleccionadasLeidas}
                  disabled={seleccionadas.size === 0 || bulkLoading}
                  data-testid="marcar-seleccionadas-btn"
                >
                  <CheckCheck className="h-4 w-4 mr-1" /> Marcar leídas
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={eliminarSeleccionadas}
                  disabled={seleccionadas.size === 0 || bulkLoading}
                  data-testid="eliminar-seleccionadas-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista */}
        {notificaciones.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-600 mb-2">No tenés notificaciones</p>
              <p className="text-gray-500">Te avisaremos cuando haya actualizaciones importantes.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {visibles.map((notif) => {
                const seleccionada = seleccionadas.has(notif.id);
                const esCobranza = TIPOS_COBRANZA.includes(notif.tipo);
                const tieneDestino = !!getDestinoSeguro(notif);
                return (
                  <Card
                    key={notif.id}
                    className={`transition-all ${!notif.leida ? 'border-l-4 border-l-sky-500 bg-sky-50/40' : ''} ${
                      tieneDestino || modoSeleccion ? 'cursor-pointer hover:shadow-md' : ''
                    } ${seleccionada ? 'ring-2 ring-sky-400 ring-offset-1' : ''}`}
                    onClick={() => handleClick(notif)}
                    data-testid={`notif-card-${notif.id}`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-4">
                        {modoSeleccion && (
                          <div onClick={(e) => e.stopPropagation()} className="pt-1">
                            <Checkbox
                              checked={seleccionada}
                              onCheckedChange={() => toggleSeleccion(notif.id)}
                              data-testid={`notif-checkbox-${notif.id}`}
                            />
                          </div>
                        )}
                        <div className={`p-2 rounded-lg ${!notif.leida ? 'bg-sky-100' : 'bg-gray-100'}`}>
                          {getIcono(notif.tipo)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className={`font-medium ${!notif.leida ? 'text-gray-900' : 'text-gray-600'}`}>
                                  {notif.titulo}
                                </p>
                                {esCobranza && (
                                  <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs">
                                    Cobranza
                                  </Badge>
                                )}
                                {tieneDestino && !modoSeleccion && (
                                  <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1 break-words">{notif.mensaje}</p>
                              <p className="text-xs text-gray-400 mt-2">
                                {new Date(notif.created_at || notif.fecha).toLocaleDateString('es-AR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                              {/* Atajo de cobranza */}
                              {esCobranza && !modoSeleccion && (
                                <Button
                                  size="sm"
                                  className="mt-3 bg-orange-500 hover:bg-orange-600 text-white"
                                  onClick={(e) => handlePagarShortcut(e, notif)}
                                  data-testid={`notif-pagar-btn-${notif.id}`}
                                >
                                  <CreditCard className="h-4 w-4 mr-1" /> Pagar ahora
                                </Button>
                              )}
                            </div>
                            {!modoSeleccion && (
                              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                {!notif.leida && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => marcarLeida(notif.id)}
                                    title="Marcar como leída"
                                    data-testid={`notif-marcar-leida-${notif.id}`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => eliminar(notif.id)}
                                  className="text-red-500 hover:text-red-700"
                                  title="Eliminar"
                                  data-testid={`notif-eliminar-${notif.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Botón Ver todas / Ver menos */}
            {notificaciones.length > PREVIEW_COUNT && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setVerTodas((v) => !v)}
                  className="text-sky-700 border-sky-300 hover:bg-sky-50"
                  data-testid="ver-todas-btn"
                >
                  {verTodas ? (
                    <><ChevronUp className="h-4 w-4 mr-1" /> Ver menos</>
                  ) : (
                    <><ChevronDown className="h-4 w-4 mr-1" /> Ver todas ({ocultas} más)</>
                  )}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Confirmación de Vaciar Bandeja */}
        <AlertDialog open={confirmDeleteAll} onOpenChange={setConfirmDeleteAll}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Vaciar tu bandeja de notificaciones?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminarán <strong>todas las notificaciones</strong> ({notificaciones.length}) de tu
                bandeja. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={eliminarTodas}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirmar-vaciar-btn"
              >
                Vaciar bandeja
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default NotificacionesPage;
